import { useCallback } from 'react'

import { pdf } from '@react-pdf/renderer'

import { useFetchDataForLocalPdfCreditNote } from 'CreditNotesList/useFetchDataForLocalPdfCreditNote'
import type { Dashboardv99990101CommonModels } from '@sequencehq/api/dashboard/v99990101'
import { logError } from '@sequencehq/utils'
import { ReactPdfCreditNote } from '@sequencehq/invoice-content'
import { isProductionEnv } from 'lib/environment/environment.ts'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

export const useGeneratePDF = () => {
  const { fetchData } = useFetchDataForLocalPdfCreditNote()
  const { showServicePeriodsAlways } = useFeatureFlags()

  const generateLocalPDF = useCallback(
    async ({ id: creditNoteId }: { id: string }) => {
      try {
        const pdfCreditNoteData = await fetchData({ creditNoteId })

        const normalisedLineItems = normaliseCreditNoteLineItems(
          pdfCreditNoteData.lineItems
        )

        return await pdf(
          <ReactPdfCreditNote
            creditNote={pdfCreditNoteData.creditNote}
            lineItems={normalisedLineItems}
            lineItemGroups={pdfCreditNoteData.lineItemGroups}
            merchantDetails={pdfCreditNoteData.merchantDetails}
            showNonProdWatermark={!isProductionEnv()}
            isRenderedInBrowser={true}
            alwaysShowServicePeriods={showServicePeriodsAlways}
          />
        ).toBlob()
      } catch (e) {
        logError(e)
      }
    },
    [fetchData, showServicePeriodsAlways]
  )

  const generateRemotePDF = useCallback(
    async ({ id: creditNoteId }: { id: string }) => {
      try {
        const creditNotePdfRes = await dashboard20240730Client.getCreditNotePdf(
          {
            id: creditNoteId
          }
        )

        if (!creditNotePdfRes.data) {
          throw new Error('An error occurred while downloading the credit note')
        }
        return creditNotePdfRes.data
      } catch (e) {
        logError(e)
      }
    },
    []
  )

  return {
    generateRemotePDF,
    generateLocalPDF
  }
}

type ApiCreditNoteLineItem =
  Dashboardv99990101CommonModels.CreditNoteLineItems.CreditNoteLineItem

/**
 * Dashboard API returns service periods as a nested object
 * ({ start, endInclusive }), but the PDF renderer expects flat
 * fields (servicePeriodStart, servicePeriodEnd).
 */
export const normaliseCreditNoteLineItems = (
  lineItems: ApiCreditNoteLineItem[]
) =>
  lineItems.map(li => ({
    ...li,
    servicePeriodStart: li.servicePeriod?.start,
    servicePeriodEnd: li.servicePeriod?.endInclusive
  }))
